import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import React from 'react';
import { NodeViewWrapper } from '@tiptap/react';
import { FiMinus, FiMoreHorizontal, FiStar } from 'react-icons/fi';

const DIVIDER_STYLES = {
  line: {
    icon: FiMinus,
    label: 'Simple Line',
    className: 'divider-line-simple',
  },
  dots: {
    icon: FiMoreHorizontal,
    label: 'Dots',
    className: 'divider-dots',
  },
  stars: {
    icon: FiStar,
    label: 'Stars',
    className: 'divider-stars',
  },
  text: {
    icon: FiMinus,
    label: 'With Text',
    className: 'divider-text',
  },
};

// Divider Node View Component
const DividerNodeView = ({ node, updateAttributes, selected }) => {
  const { style, text } = node.attrs;
  const dividerConfig = DIVIDER_STYLES[style] || DIVIDER_STYLES.line;

  const handleStyleChange = (newStyle) => {
    updateAttributes({ style: newStyle });
  };

  const handleTextChange = (e) => {
    updateAttributes({ text: e.target.value });
  };

  const renderDivider = () => {
    switch (style) {
      case 'dots':
        return (
          <div className="divider-block divider-dots">
            <span>•</span>
            <span>•</span>
            <span>•</span>
          </div>
        );
      case 'stars':
        return (
          <div className="divider-block divider-stars">
            <FiStar size={14} />
            <FiStar size={14} />
            <FiStar size={14} />
          </div>
        );
      case 'text':
        return (
          <div className="divider-block divider-with-text">
            <div className="divider-line" />
            <input
              type="text"
              value={text}
              onChange={handleTextChange}
              placeholder="Section"
              className="divider-text-input"
            />
            <div className="divider-line" />
          </div>
        );
      default:
        return (
          <div className="divider-block">
            <div className="divider-line" />
          </div>
        );
    }
  };

  return (
    <NodeViewWrapper className={`divider-wrapper ${selected ? 'selected' : ''}`}>
      <div className="divider-style-selector">
        {Object.entries(DIVIDER_STYLES).map(([key, config]) => {
          const Icon = config.icon;
          return (
            <button
              key={key}
              type="button"
              className={`divider-style-btn ${style === key ? 'active' : ''}`}
              onClick={() => handleStyleChange(key)}
              title={config.label}
            >
              <Icon size={14} />
            </button>
          );
        })}
      </div>
      {renderDivider()}
    </NodeViewWrapper>
  );
};

// Divider Extension
export const Divider = Node.create({
  name: 'dividerBlock',
  group: 'block',
  atom: true,
  draggable: true,

  addAttributes() {
    return {
      style: {
        default: 'line',
      },
      text: {
        default: '',
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-type="divider"]',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, { 'data-type': 'divider' }), 0];
  },

  addNodeView() {
    return ReactNodeViewRenderer(DividerNodeView);
  },

  addCommands() {
    return {
      insertDivider:
        (attrs = {}) =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: {
              style: attrs.style || 'line',
              text: attrs.text || '',
            },
          });
        },
    };
  },
});

export default Divider;
